import React, { useState, useEffect, useCallback, useMemo } from "react";
import { lessonAPI, assetAPI } from "../../services/api";

// Deterministic color slot for any type string (6 options, consistent per string)
function typeBadgeColor(type) {
  const colors = ["blue", "green", "amber", "purple", "rose", "teal"];
  let hash = 0;
  for (let i = 0; i < type.length; i++) {
    hash = (hash * 31 + type.charCodeAt(i)) & 0xffffffff;
  }
  return colors[Math.abs(hash) % colors.length];
}

const EMPTY_FORM = {
  lesson_id: "",
  type: "",
  title: "",
  url: "",
  instructions: "",
};

// Extract YouTube video ID from any YouTube URL variant
function getYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /[?&]v=([^&#]+)/,
    /youtu\.be\/([^?&#]+)/,
    /youtube\.com\/embed\/([^?&#]+)/,
    /youtube\.com\/shorts\/([^?&#]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

function AssetViewModal({ asset, lesson, onClose }) {
  const ytId = getYouTubeId(asset.url);

  // Close on backdrop click
  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  // Close on Escape
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="asset-view-backdrop" onClick={handleBackdrop}>
      <div className="asset-view-panel">
        {/* Header */}
        <div className="asset-view-header">
          <div className="asset-view-header-left">
            {asset.type && (
              <span className={`assets-type-badge assets-type-${typeBadgeColor(asset.type)} asset-view-type`}>
                {asset.type}
              </span>
            )}
            <h3 className="asset-view-title">{asset.title}</h3>
          </div>
          <button className="asset-view-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        {/* Lesson context */}
        {lesson && (
          <p className="asset-view-context">
            {lesson.unit_number != null && (
              <span className="assets-unit-badge">U{lesson.unit_number}</span>
            )}
            {lesson.lesson_title}
          </p>
        )}

        {/* Media / URL section */}
        {asset.url && (
          <div className="asset-view-media">
            {ytId ? (
              <div className="asset-view-video-wrap">
                <iframe
                  className="asset-view-video"
                  src={`https://www.youtube.com/embed/${ytId}`}
                  title={asset.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <a
                href={asset.url}
                target="_blank"
                rel="noopener noreferrer"
                className="asset-view-open-btn"
              >
                Open {asset.type || "Resource"} ↗
              </a>
            )}

            {/* Show URL regardless (below the embed) */}
            <p className="asset-view-url-label">
              <a
                href={asset.url}
                target="_blank"
                rel="noopener noreferrer"
                className="assets-url-link"
              >
                {asset.url}
              </a>
            </p>
          </div>
        )}

        {/* Instructions */}
        {asset.instructions && (
          <div className="asset-view-instructions">
            <h4 className="asset-view-section-title">Instructions</h4>
            <p className="asset-view-instructions-text">{asset.instructions}</p>
          </div>
        )}

        {!asset.url && !asset.instructions && (
          <p className="asset-view-empty">No URL or instructions provided for this asset.</p>
        )}
      </div>
    </div>
  );
}

function AssetsTab({ courseId }) {
  const [lessons, setLessons] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(true);
  const [loadingAssets, setLoadingAssets] = useState(true);

  // Filters
  const [filterLessonId, setFilterLessonId] = useState("");
  const [filterType, setFilterType] = useState("");

  // View modal
  const [viewingAsset, setViewingAsset] = useState(null);

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  // Edit form
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  // Delete
  const [deletingId, setDeletingId] = useState(null);

  // Load lessons for the course (for selectors)
  useEffect(() => {
    if (!courseId) return;
    setLoadingLessons(true);
    setLessons([]);
    setFilterLessonId("");
    lessonAPI
      .getAll({ courseId })
      .then((r) => setLessons(r.data || []))
      .catch(() => setLessons([]))
      .finally(() => setLoadingLessons(false));
  }, [courseId]);

  // Load assets for the course
  const loadAssets = useCallback(() => {
    if (!courseId) return;
    setLoadingAssets(true);
    assetAPI
      .getAll({ courseId })
      .then((r) => setAssets(r.data || []))
      .catch(() => setAssets([]))
      .finally(() => setLoadingAssets(false));
  }, [courseId]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  // Fast lookup: lesson_id → lesson row (for display in table rows)
  const lessonsById = useMemo(() => {
    const map = {};
    lessons.forEach((l) => { map[l.id] = l; });
    return map;
  }, [lessons]);

  // Unique type strings present in the loaded assets, sorted (drives filter + datalist)
  const assetTypes = useMemo(() => {
    const seen = new Set();
    assets.forEach((a) => { if (a.type) seen.add(a.type); });
    return [...seen].sort();
  }, [assets]);

  // Derived filtered list
  const filteredAssets = assets.filter((a) => {
    if (filterLessonId && a.lesson_id !== filterLessonId) return false;
    if (filterType && a.type !== filterType) return false;
    return true;
  });

  // Add
  function openAddForm() {
    setAddForm({
      ...EMPTY_FORM,
      lesson_id: lessons[0]?.id || "",
      type: assetTypes[0] || "",
    });
    setAddError("");
    setShowAddForm(true);
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!addForm.lesson_id) return setAddError("Select a lesson.");
    if (!addForm.title.trim()) return setAddError("Title is required.");
    setAdding(true);
    setAddError("");
    try {
      await assetAPI.create({
        lesson_id: addForm.lesson_id,
        type: addForm.type || null,
        title: addForm.title.trim(),
        url: addForm.url.trim() || null,
        instructions: addForm.instructions.trim() || null,
      });
      setShowAddForm(false);
      setAddForm(EMPTY_FORM);
      loadAssets();
    } catch (err) {
      setAddError(err?.response?.data?.error || "Failed to add asset.");
    } finally {
      setAdding(false);
    }
  }

  // Edit
  function startEdit(asset) {
    setEditingId(asset.id);
    setEditForm({
      lesson_id: asset.lesson_id || "",
      type: asset.type || "",
      title: asset.title || "",
      url: asset.url || "",
      instructions: asset.instructions || "",
    });
    setEditError("");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditError("");
  }

  async function handleEditSave(e) {
    e.preventDefault();
    if (!editForm.title.trim()) return setEditError("Title is required.");
    setEditSaving(true);
    setEditError("");
    try {
      await assetAPI.update(editingId, {
        lesson_id: editForm.lesson_id,
        type: editForm.type || null,
        title: editForm.title.trim(),
        url: editForm.url.trim() || null,
        instructions: editForm.instructions.trim() || null,
      });
      setEditingId(null);
      loadAssets();
    } catch (err) {
      setEditError(err?.response?.data?.error || "Failed to save.");
    } finally {
      setEditSaving(false);
    }
  }

  // Delete
  async function handleDelete(id) {
    if (!window.confirm("Delete this asset?")) return;
    setDeletingId(id);
    try {
      await assetAPI.delete(id);
      setAssets((prev) => prev.filter((a) => a.id !== id));
    } catch {
      alert("Failed to delete asset.");
    } finally {
      setDeletingId(null);
    }
  }

  if (!courseId) {
    return <p className="curriculum-empty">Select a course to manage assets.</p>;
  }

  return (
    <div className="assets-tab">
      {/* View modal */}
      {viewingAsset && (
        <AssetViewModal
          asset={viewingAsset}
          lesson={lessonsById[viewingAsset.lesson_id] || null}
          onClose={() => setViewingAsset(null)}
        />
      )}

      {/* Filter bar */}
      <div className="assets-filter-bar">
        <label className="assets-filter-label">
          Lesson
          <select
            value={filterLessonId}
            onChange={(e) => setFilterLessonId(e.target.value)}
            className="assets-filter-select"
            disabled={loadingLessons}
          >
            <option value="">All lessons</option>
            {lessons.map((l) => (
              <option key={l.id} value={l.id}>
                {l.unit_number != null ? `U${l.unit_number} — ` : ""}
                {l.lesson_title}
              </option>
            ))}
          </select>
        </label>

        <label className="assets-filter-label">
          Type
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="assets-filter-select"
          >
            <option value="">All types</option>
            {assetTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>

        <button className="assets-add-btn" onClick={openAddForm}>
          + Add Asset
        </button>
      </div>

      {/* Add form */}
      {showAddForm && (
        <form className="assets-add-form" onSubmit={handleAdd}>
          <h4 className="assets-add-form-title">New Asset</h4>
          <div className="assets-form-row">
            <label className="assets-form-label">
              Lesson
              <select
                value={addForm.lesson_id}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, lesson_id: e.target.value }))
                }
                className="assets-form-select"
                required
              >
                <option value="">— select —</option>
                {lessons.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.unit_number != null ? `U${l.unit_number} — ` : ""}
                    {l.lesson_title}
                  </option>
                ))}
              </select>
            </label>

            <label className="assets-form-label">
              Type
              <input
                list="asset-types-list"
                value={addForm.type}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, type: e.target.value }))
                }
                className="assets-form-input"
                placeholder="e.g. District Resource"
              />
              <datalist id="asset-types-list">
                {assetTypes.map((t) => <option key={t} value={t} />)}
              </datalist>
            </label>
          </div>

          <div className="assets-form-row">
            <label className="assets-form-label assets-form-label--full">
              Title
              <input
                type="text"
                value={addForm.title}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, title: e.target.value }))
                }
                className="assets-form-input"
                placeholder="Asset title"
                required
              />
            </label>
          </div>

          <div className="assets-form-row">
            <label className="assets-form-label assets-form-label--full">
              URL
              <input
                type="url"
                value={addForm.url}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, url: e.target.value }))
                }
                className="assets-form-input"
                placeholder="https://..."
              />
            </label>
          </div>

          <div className="assets-form-row">
            <label className="assets-form-label assets-form-label--full">
              Instructions
              <textarea
                value={addForm.instructions}
                onChange={(e) =>
                  setAddForm((f) => ({ ...f, instructions: e.target.value }))
                }
                className="assets-form-textarea"
                rows={3}
                placeholder="Optional instructions for using this asset..."
              />
            </label>
          </div>

          {addError && <p className="assets-form-error">{addError}</p>}

          <div className="assets-form-actions">
            <button type="submit" className="assets-save-btn" disabled={adding}>
              {adding ? "Adding..." : "Add Asset"}
            </button>
            <button
              type="button"
              className="assets-cancel-btn"
              onClick={() => setShowAddForm(false)}
              disabled={adding}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Assets table */}
      {loadingAssets ? (
        <p className="curriculum-empty">Loading assets...</p>
      ) : filteredAssets.length === 0 ? (
        <p className="curriculum-empty">
          {assets.length === 0
            ? "No assets yet for this course. Click + Add Asset to create one."
            : "No assets match the current filters."}
        </p>
      ) : (
        <div className="assets-table-wrap">
          <table className="assets-table">
            <thead>
              <tr>
                <th>Lesson</th>
                <th>Type</th>
                <th>Title</th>
                <th>URL</th>
                <th>Instructions</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredAssets.map((asset) =>
                editingId === asset.id ? (
                  <tr key={asset.id} className="assets-table-edit-row">
                    <td>
                      <select
                        value={editForm.lesson_id}
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, lesson_id: e.target.value }))
                        }
                        className="assets-form-select"
                      >
                        {editForm.lesson_id && !lessonsById[editForm.lesson_id] && (
                          <option value={editForm.lesson_id}>
                            (current lesson — id: {editForm.lesson_id.slice(0, 8)}…)
                          </option>
                        )}
                        {lessons.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.unit_number != null ? `U${l.unit_number} — ` : ""}
                            {l.lesson_title}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        list="asset-types-list"
                        value={editForm.type}
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, type: e.target.value }))
                        }
                        className="assets-form-input"
                        placeholder="Type"
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={editForm.title}
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, title: e.target.value }))
                        }
                        className="assets-form-input"
                      />
                    </td>
                    <td>
                      <input
                        type="url"
                        value={editForm.url}
                        onChange={(e) =>
                          setEditForm((f) => ({ ...f, url: e.target.value }))
                        }
                        className="assets-form-input"
                        placeholder="https://..."
                      />
                    </td>
                    <td>
                      <textarea
                        value={editForm.instructions}
                        onChange={(e) =>
                          setEditForm((f) => ({
                            ...f,
                            instructions: e.target.value,
                          }))
                        }
                        className="assets-form-textarea"
                        rows={2}
                      />
                    </td>
                    <td className="assets-table-actions">
                      {editError && (
                        <span className="assets-inline-error">{editError}</span>
                      )}
                      <button
                        className="assets-save-btn assets-save-btn--sm"
                        onClick={handleEditSave}
                        disabled={editSaving}
                      >
                        {editSaving ? "..." : "Save"}
                      </button>
                      <button
                        className="assets-cancel-btn assets-cancel-btn--sm"
                        onClick={cancelEdit}
                        disabled={editSaving}
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ) : (
                  <tr key={asset.id}>
                    <td className="assets-cell-lesson">
                      {(() => {
                        const lesson = lessonsById[asset.lesson_id];
                        const unitNum = lesson?.unit_number ?? null;
                        const title = lesson?.lesson_title || "—";
                        return (
                          <>
                            {unitNum != null && (
                              <span className="assets-unit-badge">U{unitNum}</span>
                            )}
                            {title}
                          </>
                        );
                      })()}
                    </td>
                    <td>
                      {asset.type ? (
                        <span className={`assets-type-badge assets-type-${typeBadgeColor(asset.type)}`}>
                          {asset.type}
                        </span>
                      ) : (
                        <span className="assets-type-none">—</span>
                      )}
                    </td>
                    <td className="assets-cell-title">{asset.title}</td>
                    <td className="assets-cell-url">
                      {asset.url ? (
                        <a
                          href={asset.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="assets-url-link"
                        >
                          {truncateUrl(asset.url)}
                        </a>
                      ) : (
                        <span className="assets-type-none">—</span>
                      )}
                    </td>
                    <td className="assets-cell-instructions">
                      {asset.instructions ? (
                        <span title={asset.instructions}>
                          {asset.instructions.length > 80
                            ? asset.instructions.slice(0, 80) + "…"
                            : asset.instructions}
                        </span>
                      ) : (
                        <span className="assets-type-none">—</span>
                      )}
                    </td>
                    <td className="assets-table-actions">
                      <button
                        className="assets-action-btn"
                        onClick={() => setViewingAsset(asset)}
                      >
                        View
                      </button>
                      <button
                        className="assets-action-btn"
                        onClick={() => startEdit(asset)}
                      >
                        Edit
                      </button>
                      <button
                        className="assets-action-btn assets-action-btn--delete"
                        onClick={() => handleDelete(asset.id)}
                        disabled={deletingId === asset.id}
                      >
                        {deletingId === asset.id ? "..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function truncateUrl(url) {
  try {
    const u = new URL(url);
    const path = u.pathname.length > 30 ? u.pathname.slice(0, 30) + "…" : u.pathname;
    return u.hostname + path;
  } catch {
    return url.length > 50 ? url.slice(0, 50) + "…" : url;
  }
}

export default AssetsTab;
